import { useState, useEffect, useRef, useCallback } from 'react'
import { v4 as uuidv4 } from 'uuid'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
  ActionIcon,
  Alert,
  Button,
  Stack,
  Switch,
  Textarea,
} from '@mantine/core'
import {
  IconAlertCircle,
  IconChevronRight,
  IconCircleCheck,
  IconEdit,
  IconMessages,
} from '@tabler/icons-react'
import styles from './ClairChat.module.css'
import { WsMessage, ChatMessage } from './types'
import { useIsAdmin } from '../widgets/api'
import Header, { ClairMobileHeader } from '../common/components/header/Header'
import Meta from '../common/Meta'
import { useDisclosure, useMediaQuery } from '@mantine/hooks'
import { Typography } from '../common/typography'
import clsx from 'clsx'
import { ReactComponent as ClairLogo } from '../assets/clair_logo.svg'
import { LeaguestarState } from '../state'
import ChatHistory from './ChatHistory'
import { Capacitor } from '@capacitor/core'
import { Preferences } from '@capacitor/preferences'
import axios from 'axios'

const getWsUrl = async () => {
  // On native, the webview origin is capacitor://localhost, not the backend.
  // Mirror axios-client.ts: derive the WS host from the stored apiBaseUrl.
  if (Capacitor.isNativePlatform()) {
    const { value } = await Preferences.get({ key: 'apiBaseUrl' })
    const base = value || process.env.REACT_APP_API_URL
    if (base) {
      const url = new URL(base)
      const protocol = url.protocol === 'https:' ? 'wss:' : 'ws:'
      return `${protocol}//${url.host}/ws`
    }
  }

  const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:'
  const host = window.location.host
  return `${protocol}//${host}/ws`
}

export default function ClairChat() {
  const { data: adminCheck } = useIsAdmin()

  // Check if user is admin
  if (!adminCheck?.is_admin) {
    return (
      <Stack p="xl" maw="600px">
        <Alert icon={<IconAlertCircle size="1rem" />} title="Error" color="red">
          Page content not found. Please contact support.
        </Alert>
      </Stack>
    )
  }

  const isDesktop = useMediaQuery('(min-width: 800px)')
  const isTablet = useMediaQuery('(max-width: 1119px)')
  const [opened, { open, close }] = useDisclosure(false)
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [inputValue, setInputValue] = useState('')
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const [sessionId, setSessionId] = useState<string | null>(null)
  const [collapsedSystemGroups, setCollapsedSystemGroups] = useState<
    Set<number>
  >(new Set())
  const [activeSession, setActiveSession] = useState<string>('')
  const [loadingMessage, setLoadingMessage] = useState<boolean>(false)
  const [showAdditionalMessages, setShowAdditionalMessages] =
    useState<boolean>(false)

  const wsRef = useRef<WebSocket | null>(null)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    scrollToBottom()
  }, [messages])

  const addMessage = useCallback((message: ChatMessage) => {
    setMessages((prev) => [...prev, message])
  }, [])

  const handleWsMessage = useCallback(
    (event: MessageEvent) => {
      try {
        const wsMessage: WsMessage = JSON.parse(event.data)

        switch (wsMessage.type) {
          case 'connected':
            setSessionId(wsMessage.session_id || null)
            addMessage({
              id: uuidv4(),
              type: 'system',
              content: `Connected to Clair Code session: ${wsMessage.session_id}`,
              timestamp: new Date(),
            })
            break

          case 'history':
            // Handle batch historical messages
            if (wsMessage.messages && Array.isArray(wsMessage.messages)) {
              // Clear existing messages when loading history
              setMessages([])

              // Add all historical messages
              const historicalMessages: ChatMessage[] = wsMessage.messages.map(
                (msg) => ({
                  id: uuidv4(),
                  type: msg.type as 'user' | 'assistant' | 'system' | 'error',
                  content: msg.content,
                  timestamp: new Date(), // Historical messages get current timestamp for simplicity
                  isHistorical: true,
                })
              )

              setMessages(historicalMessages)

              // Add a system message to indicate history was loaded
              addMessage({
                id: uuidv4(),
                type: 'system',
                content: `Loaded ${wsMessage.messages.length} messages from session history`,
                timestamp: new Date(),
              })
            }
            break

          case 'user':
          case 'assistant':
            addMessage({
              id: uuidv4(),
              type: wsMessage.type,
              content: wsMessage.content || '',
              timestamp: new Date(),
              isHistorical: wsMessage.isHistorical,
            })
            break

          case 'system':
            if (activeSession === '' && wsMessage.session_id) {
              setActiveSession(wsMessage.session_id)
            }
            addMessage({
              id: uuidv4(),
              type: wsMessage.type,
              content: wsMessage.content || '',
              timestamp: new Date(),
              isHistorical: wsMessage.isHistorical,
            })
            break

          case 'status':
            if (!loadingMessage && wsMessage.state === 'processing') {
              setLoadingMessage(true)
            } else if (wsMessage.state === 'completed') {
              setLoadingMessage(false)
            }

            break

          case 'error':
            addMessage({
              id: uuidv4(),
              type: 'error',
              content: wsMessage.message || 'Unknown error occurred',
              timestamp: new Date(),
            })
            break

          case 'thinking':
            addMessage({
              id: uuidv4(),
              type: 'thinking',
              content: wsMessage.content || '',
              timestamp: new Date(),
            })
            break

          case 'tool_use':
            addMessage({
              id: uuidv4(),
              type: 'tool_use',
              content: wsMessage.content || '',
              tool_name: wsMessage.tool_name,
              tool_input: wsMessage.tool_input,
              tool_id: wsMessage.tool_id,
              timestamp: new Date(),
            })
            break

          case 'tool_result':
            addMessage({
              id: uuidv4(),
              type: 'tool_result',
              content: wsMessage.content || '',
              tool_use_id: wsMessage.tool_use_id,
              tool_output: wsMessage.tool_output,
              timestamp: new Date(),
            })
            break

          case 'result_summary':
            addMessage({
              id: uuidv4(),
              type: 'result_summary',
              content: '', // Content will be generated from metrics
              num_turns: wsMessage.num_turns,
              duration_ms: wsMessage.duration_ms,
              duration_api_ms: wsMessage.duration_api_ms,
              total_cost_usd: wsMessage.total_cost_usd,
              usage: wsMessage.usage,
              timestamp: new Date(),
            })
            break
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error)
      }
    },
    [addMessage]
  )

  const connectWebSocket = useCallback(async () => {
    // Prevent multiple simultaneous connections
    if (
      wsRef.current?.readyState === WebSocket.OPEN ||
      wsRef.current?.readyState === WebSocket.CONNECTING ||
      isConnecting
    ) {
      return
    }

    setIsConnecting(true)

    const wsUrl = await getWsUrl()
    const currentState = LeaguestarState.getRawState()
    const platformId =
      currentState.selected_platform_id ??
      currentState.platforms[0]?.platform_id ??
      1
    const userId = currentState.user_id
    // On web the session cookie rides the WS handshake automatically. On native the
    // WebView's WebSocket can't carry the cookie, so mint a short-lived single-use
    // ticket over authenticated HTTP and pass it via the WS subprotocol (never the
    // URL, so it can't leak into access logs). The engine forwards it to the API.
    let protocols: string[] | undefined
    if (Capacitor.isNativePlatform()) {
      try {
        // platform_id is appended implicitly by the axios config - don't add it
        // here too, or the query string has a duplicate and the API 400s.
        const { data } = await axios.post('/api/clair/ws-ticket')
        const ticket = data?.data?.ticket ?? data?.ticket
        if (ticket) protocols = ['ticket', ticket]
      } catch (err) {
        console.error('Failed to mint Clair WS ticket:', err)
      }
    }
    const ws = new WebSocket(
      `${wsUrl}?platform_id=${platformId}&user_id=${userId}`,
      protocols
    )

    ws.onopen = () => {
      setIsConnected(true)
      setIsConnecting(false)
      addMessage({
        id: uuidv4(),
        type: 'system',
        content: 'Connecting to Clair Code...',
        timestamp: new Date(),
      })
    }

    ws.onmessage = handleWsMessage

    ws.onclose = () => {
      setIsConnected(false)
      setIsConnecting(false)
      setSessionId(null)
      addMessage({
        id: uuidv4(),
        type: 'system',
        content: 'Disconnected from Clair Code',
        timestamp: new Date(),
      })
    }

    ws.onerror = (error) => {
      console.error('WebSocket error:', error)
      addMessage({
        id: uuidv4(),
        type: 'error',
        content: 'Connection error. Please try again.',
        timestamp: new Date(),
      })
    }

    wsRef.current = ws
  }, [handleWsMessage, addMessage])

  const disconnectWebSocket = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
  }, [])

  useEffect(() => {
    // Prevent duplicate connections in React StrictMode
    let mounted = true

    if (mounted && !wsRef.current) {
      connectWebSocket()
    }

    return () => {
      mounted = false
      disconnectWebSocket()
    }
  }, []) // Remove dependencies to prevent re-connections

  const handleResumeSession = useCallback(
    (selectedSessionId: string) => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        // Clear current messages
        setMessages([])

        // Send resume session message
        const resumeMsg = {
          type: 'resume_session',
          session_id: selectedSessionId,
        }
        wsRef.current.send(JSON.stringify(resumeMsg))

        // Update current session ID
        setSessionId(selectedSessionId)
        setActiveSession(selectedSessionId)
        close()
      } else {
        addMessage({
          id: uuidv4(),
          type: 'error',
          content: 'Not connected to server. Please refresh and try again.',
          timestamp: new Date(),
        })
      }
    },
    [addMessage]
  )

  const sendMessage = useCallback(() => {
    const trimmedInput = inputValue.trim()

    if (!trimmedInput || !isConnected || !wsRef.current) {
      return
    }

    const userMessage: WsMessage = {
      type: 'user',
      content: trimmedInput,
    }

    // Add user message to chat immediately
    addMessage({
      id: uuidv4(),
      type: 'user',
      content: trimmedInput,
      timestamp: new Date(),
    })

    // Send message through WebSocket
    wsRef.current.send(JSON.stringify(userMessage))

    // Clear input
    setInputValue('')

    // Focus input
    inputRef.current?.focus()
  }, [inputValue, isConnected, addMessage])

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      sendMessage()
    }
  }

  const formatMessage = (content: string) => {
    return (
      <div className={styles.markdown}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code: ({
              className,
              children,
              ...props
            }: React.ComponentPropsWithoutRef<'code'>) => {
              const match = /language-(\w+)/.exec(className || '')
              const isInline = !match
              const lang = match ? match[1] : ''

              if (!isInline) {
                return (
                  <div className={styles.codeBlock}>
                    {lang && <div className={styles.codeLanguage}>{lang}</div>}
                    <pre>
                      <code className={className} {...props}>
                        {children}
                      </code>
                    </pre>
                  </div>
                )
              }

              return (
                <code className={styles.inlineCode} {...props}>
                  {children}
                </code>
              )
            },
            pre: ({ children }) => <>{children}</>,
            p: ({ children }) => <p className={styles.paragraph}>{children}</p>,
            ul: ({ children }) => <ul className={styles.list}>{children}</ul>,
            ol: ({ children }) => (
              <ol className={styles.orderedList}>{children}</ol>
            ),
            li: ({ children }) => (
              <li className={styles.listItem}>{children}</li>
            ),
            blockquote: ({ children }) => (
              <blockquote className={styles.blockquote}>{children}</blockquote>
            ),
            table: ({ children }) => (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>{children}</table>
              </div>
            ),
            a: ({ href, children }) => (
              <a
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                className={styles.link}
              >
                {children}
              </a>
            ),
          }}
        >
          {content}
        </ReactMarkdown>
      </div>
    )
  }

  // Group consecutive system messages

  const groupedMessages = messages.reduce<
    Array<
      | ChatMessage
      | { type: 'system-group'; messages: ChatMessage[]; groupId: number }
    >
  >((acc, message, index) => {
    // Group system messages, errors, tool use, tool results, and result summaries together
    if (
      message.type === 'system' ||
      message.type === 'error' ||
      message.type === 'tool_use' ||
      message.type === 'tool_result' ||
      message.type === 'result_summary'
    ) {
      const lastItem = acc[acc.length - 1]
      if (
        lastItem &&
        'groupId' in lastItem &&
        lastItem.type === 'system-group'
      ) {
        lastItem.messages.push(message)
      } else {
        acc.push({
          type: 'system-group',
          messages: [message],
          groupId: index,
        })
      }
    } else {
      // User, assistant, and thinking messages are displayed separately
      acc.push(message)
    }
    return acc
  }, [])

  const toggleSystemGroup = (groupId: number) => {
    setCollapsedSystemGroups((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(groupId)) {
        newSet.delete(groupId)
      } else {
        newSet.add(groupId)
      }
      return newSet
    })
  }

  // Auto-collapse new system message groups
  useEffect(() => {
    const systemGroups = groupedMessages.filter((item) => 'groupId' in item)
    systemGroups.forEach((group) => {
      if ('groupId' in group && !collapsedSystemGroups.has(group.groupId)) {
        setCollapsedSystemGroups((prev) => new Set(prev).add(group.groupId))
      }
    })
  }, [groupedMessages.length])

  return (
    <div className={styles.container}>
      {isDesktop ? (
        <Header title="Ask Clair" />
      ) : (
        <ClairMobileHeader title="Ask Clair" open={open} />
      )}
      <Meta title="Clair" />
      {isDesktop && (
        <div className={styles.clairHeader}>
          <Button
            leftSection={<IconEdit className={styles.iconButton} />}
            classNames={{
              root: styles.button,
              section: styles.section,
            }}
            onClick={() => {
              if (wsRef.current?.readyState === WebSocket.OPEN) {
                // Send new_chat message
                wsRef.current.send(JSON.stringify({ type: 'new_chat' }))
                // Clear UI state - no messages added
                setMessages([])
                setSessionId(null)
                setActiveSession('')
              }
            }}
          >
            <Typography
              fc={{ light: 'white', dark: 'white' }}
              fw={500}
              lh="125"
            >
              New Chat
            </Typography>
          </Button>
          <Button
            classNames={{
              root: styles.buttonTransparent,
              section: styles.section,
            }}
            leftSection={
              <IconMessages
                className={clsx(
                  styles.iconButton,
                  styles.iconButtonTransparent
                )}
              />
            }
            onClick={open}
          >
            <Typography
              fc={{ light: 'primary-5', dark: 'primary-4' }}
              fw={500}
              lh="125"
            >
              Chats
            </Typography>
          </Button>
        </div>
      )}

      {adminCheck.is_admin && (
        <Switch
          checked={showAdditionalMessages}
          onChange={(event) =>
            setShowAdditionalMessages(event.currentTarget.checked)
          }
          label="Show additional messages"
          labelPosition="left"
          classNames={{
            root: styles.switchRoot,
            label: styles.switchLabel,
            body: styles.switchBody,
            track: styles.switchTrack,
            thumb: styles.switchThumb,
          }}
        />
      )}
      {isConnected && showAdditionalMessages && (
        <Alert
          icon={<IconCircleCheck />}
          color="green"
          classNames={{
            root: clsx(styles.alertRoot, {
              [styles.alertRootMobile]: !isDesktop,
            }),
            wrapper: styles.alertWrapper,
            message: styles.alertMessage,
            icon: styles.alertIcon,
          }}
        >
          Connected {sessionId ? ` (${sessionId.substring(0, 8)}...)` : ''}
        </Alert>
      )}
      {!isConnected && (
        <Alert
          icon={<IconAlertCircle />}
          color="red"
          classNames={{
            root: clsx(styles.alertRoot, {
              [styles.alertRootMobile]: !isDesktop,
            }),
            wrapper: styles.alertWrapper,
            message: styles.alertMessage,
            icon: styles.alertIcon,
          }}
        >
          You are currently disconnected.
          <Button
            classNames={{
              root: styles.button,
              section: styles.section,
            }}
            loading={isConnecting}
            onClick={connectWebSocket}
          >
            <Typography
              fc={{ light: 'white', dark: 'white' }}
              fw={500}
              lh="125"
            >
              Connect
            </Typography>
          </Button>
        </Alert>
      )}
      <div
        className={clsx(
          styles.wrapper,
          {
            [styles.wrapperWithMessagesMobile]:
              groupedMessages.length > 0 &&
              groupedMessages.some(
                (message) =>
                  message.type === 'assistant' || message.type === 'user'
              ) &&
              !isDesktop,
          },
          {
            [styles.wrapperWithMessages]:
              groupedMessages.length > 0 &&
              groupedMessages.some(
                (message) =>
                  message.type === 'assistant' || message.type === 'user'
              ),
          }
          // this will be used if we remove information about connection
          // { [styles.wrapperMobile]: !isDesktop  }
        )}
      >
        <div className={styles.messagesContainer}>
          {groupedMessages.map((item) => {
            if ('groupId' in item) {
              if (!showAdditionalMessages) {
                return null
              }

              const isCollapsed = collapsedSystemGroups.has(item.groupId)
              return (
                <div
                  key={`group-${item.groupId}`}
                  className={styles.systemGroup}
                >
                  <button
                    className={styles.systemGroupToggle}
                    onClick={() => toggleSystemGroup(item.groupId)}
                  >
                    <span className={styles.toggleIcon}>
                      {isCollapsed ? '▶' : '▼'}
                    </span>
                    <span className={styles.systemGroupLabel}>
                      System messages ({item.messages.length})
                    </span>
                    {isCollapsed && (
                      <span className={styles.systemGroupPreview}>
                        {item.messages[
                          item.messages.length - 1
                        ].content.substring(0, 50)}
                        {item.messages[item.messages.length - 1].content
                          .length > 50
                          ? '...'
                          : ''}
                      </span>
                    )}
                  </button>
                  {!isCollapsed && (
                    <div className={styles.systemGroupMessages}>
                      {item.messages.map((message) => {
                        // Render tool use message
                        if (message.type === 'tool_use') {
                          return (
                            <div
                              key={message.id}
                              className={`${styles.message} ${styles.toolUseMessage}`}
                            >
                              <div>
                                <div className={styles.messageLabel}>
                                  Tool Use: {message.tool_name}
                                </div>
                                <div className={styles.messageContent}>
                                  <div>
                                    <strong>Input:</strong>
                                    <pre className={styles.toolInput}>
                                      {JSON.stringify(
                                        message.tool_input,
                                        null,
                                        2
                                      )}
                                    </pre>
                                  </div>
                                </div>
                                <div className={styles.timestamp}>
                                  {message.timestamp.toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                          )
                        }

                        // Render tool result message
                        if (message.type === 'tool_result') {
                          return (
                            <div
                              key={message.id}
                              className={`${styles.message} ${styles.toolResultMessage}`}
                            >
                              <div>
                                <div className={styles.messageLabel}>
                                  Tool Result
                                </div>
                                <div className={styles.messageContent}>
                                  <div>
                                    <strong>Output:</strong>
                                    <pre className={styles.toolOutput}>
                                      {typeof message.tool_output === 'string'
                                        ? message.tool_output
                                        : JSON.stringify(
                                            message.tool_output,
                                            null,
                                            2
                                          )}
                                    </pre>
                                  </div>
                                </div>
                                <div className={styles.timestamp}>
                                  {message.timestamp.toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                          )
                        }

                        // Render result summary message
                        if (message.type === 'result_summary') {
                          const cost = message.total_cost_usd
                            ? `$${message.total_cost_usd.toFixed(4)}`
                            : 'N/A'
                          const apiTime = message.duration_api_ms
                            ? ` (API: ${message.duration_api_ms}ms)`
                            : ''

                          return (
                            <div
                              key={message.id}
                              className={`${styles.message} ${styles.resultSummaryMessage}`}
                            >
                              <div>
                                <div className={styles.messageLabel}>
                                  Result Summary
                                </div>
                                <div className={styles.messageContent}>
                                  <div className={styles.metricsGrid}>
                                    <div>
                                      <strong>Turns:</strong>{' '}
                                      {message.num_turns}
                                    </div>
                                    <div>
                                      <strong>Duration:</strong>{' '}
                                      {message.duration_ms}ms{apiTime}
                                    </div>
                                    <div>
                                      <strong>Cost:</strong> {cost}
                                    </div>
                                    {message.usage && (
                                      <>
                                        <div>
                                          <strong>Input tokens:</strong>{' '}
                                          {message.usage.input_tokens}
                                          {message.usage
                                            .cache_read_input_tokens && (
                                            <span
                                              style={{
                                                color:
                                                  'var(--mantine-color-green-6)',
                                              }}
                                            >
                                              {' '}
                                              (cache:{' '}
                                              {
                                                message.usage
                                                  .cache_read_input_tokens
                                              }
                                              )
                                            </span>
                                          )}
                                        </div>
                                        <div>
                                          <strong>Output tokens:</strong>{' '}
                                          {message.usage.output_tokens}
                                        </div>
                                        {message.usage
                                          .cache_creation_input_tokens && (
                                          <div>
                                            <strong>Cache created:</strong>{' '}
                                            {
                                              message.usage
                                                .cache_creation_input_tokens
                                            }{' '}
                                            tokens
                                          </div>
                                        )}
                                      </>
                                    )}
                                  </div>
                                </div>
                                <div className={styles.timestamp}>
                                  {message.timestamp.toLocaleTimeString()}
                                </div>
                              </div>
                            </div>
                          )
                        }

                        // Render regular system/error messages
                        return (
                          <div
                            key={message.id}
                            className={`${styles.message} ${
                              message.type === 'system'
                                ? styles.systemMessage
                                : styles.errorMessage
                            }`}
                          >
                            <div>
                              <div className={styles.messageLabel}>
                                {message.type === 'system' ? 'System' : 'Error'}
                              </div>
                              <div className={styles.messageContent}>
                                {formatMessage(message.content)}
                              </div>
                              <div className={styles.timestamp}>
                                {message.timestamp.toLocaleTimeString()}
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            } else if (item.type === 'thinking') {
              // Thinking block message
              return (
                <div key={item.id} className={styles.thinkingMessage}>
                  <div className={styles.thinkingHeader}>
                    <Typography
                      fc={{ light: 'gray-6', dark: 'gray-5' }}
                      fw={600}
                    >
                      💭 Thinking
                    </Typography>
                  </div>
                  <Typography fc={{ light: 'gray-6', dark: 'gray-5' }} lh="150">
                    {formatMessage(item.content)}
                  </Typography>
                </div>
              )
            } else {
              // Regular user/assistant message
              return (
                <div
                  key={item.id}
                  className={clsx(
                    { [styles.userMessage]: item.type === 'user' },
                    { [styles.assistantMessage]: item.type === 'assistant' }
                  )}
                >
                  <Typography
                    fc={{
                      light: item.type === 'user' ? 'gray-3' : 'gray-7',
                      dark: item.type === 'user' ? 'gray-3' : 'gray-7',
                    }}
                    lh="150"
                  >
                    {formatMessage(item.content)}
                  </Typography>
                </div>
              )
            }
          })}
          {loadingMessage && (
            <div className={styles.loaderContainer}>
              <div className={styles.loader} />
              <Typography
                fc={{
                  light: 'gray-7',
                  dark: 'gray-3',
                }}
                lh="150"
              >
                Working on it
              </Typography>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
        {(groupedMessages.length === 0 ||
          !groupedMessages.some(
            (message) => message.type === 'assistant' || message.type === 'user'
          )) && (
          <div className={styles.clairLogoContainer}>
            <ClairLogo />
          </div>
        )}

        <div
          className={clsx(
            styles.inputContainer,
            {
              [styles.inputWithMessages]:
                groupedMessages.length > 0 &&
                groupedMessages.some(
                  (message) =>
                    message.type === 'assistant' || message.type === 'user'
                ),
            },
            {
              [styles.inputWithMessagesMobile]:
                groupedMessages.length > 0 &&
                groupedMessages.some(
                  (message) =>
                    message.type === 'assistant' || message.type === 'user'
                ) &&
                isTablet,
            }
          )}
        >
          <Textarea
            ref={inputRef}
            classNames={{
              root: styles.textareaRoot,
              wrapper: styles.textareaWrapper,
              input: styles.textareaInput,
            }}
            className={styles.input}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={
              isConnected
                ? 'How can I help you?'
                : 'Connect to start chatting...'
            }
            disabled={!isConnected}
            autosize
          />
          <ActionIcon
            variant="filled"
            classNames={{
              root: styles.sendButton,
              icon: styles.sendButtonIcon,
            }}
            disabled={!isConnected || !inputValue.trim() || loadingMessage}
            onClick={sendMessage}
          >
            <IconChevronRight />
          </ActionIcon>
        </div>
      </div>
      <ChatHistory
        opened={opened}
        close={close}
        activeSession={activeSession}
        handleResumeSession={handleResumeSession}
      />
    </div>
  )
}
